/**
 * Servicio principal de sincronización de transacciones
 */

import { Transaction } from '@/types/transaction.types';
import { TransactionProviderFactory } from './transaction-provider-factory';
import { TransactionCacheService } from './transaction-cache.service';
import { SYNC_CONFIG } from '@/config/transaction-networks';
import { DEPRECATED_TOKEN_ADDRESSES } from '@/config/tokens';
import WalletManager from '../wallet/wallet-manager';
import { useWalletStore } from '@/store/walletStore';

export class TransactionSyncService {
  private static instance: TransactionSyncService;
  private syncIntervals: Map<string, NodeJS.Timeout> = new Map();
  private isSyncing: Map<string, boolean> = new Map();

  private constructor() { }

  static getInstance(): TransactionSyncService {
    if (!TransactionSyncService.instance) {
      TransactionSyncService.instance = new TransactionSyncService();
    }
    return TransactionSyncService.instance;
  }

  /**
   * Inicia la sincronización automática; devuelve la promesa de la sincronización inicial.
   */
  startAutoSync(walletAddress: string): Promise<void> {
    console.log(`[TransactionSync] Starting auto sync for wallet ${walletAddress}`);

    // Detener sincronización existente si existe
    this.stopAutoSync(walletAddress);

    // Sincronización inicial
    const initialSync = this.syncAllNetworks(walletAddress);

    // Configurar intervalo de sincronización
    const interval = setInterval(() => {
      this.syncAllNetworks(walletAddress);
    }, this.getSyncInterval(walletAddress));

    this.syncIntervals.set(walletAddress, interval);

    return initialSync;
  }

  /**
   * Detiene la sincronización automática para una wallet
   */
  stopAutoSync(walletAddress: string): void {
    const interval = this.syncIntervals.get(walletAddress);
    if (interval) {
      clearInterval(interval);
      this.syncIntervals.delete(walletAddress);
      console.log(`[TransactionSync] Stopped auto sync for wallet ${walletAddress}`);
    }
  }

  /**
   * Sincroniza transacciones para todas las redes soportadas
   */
  async syncAllNetworks(walletAddress: string): Promise<void> {
    if (!this.shouldSync(walletAddress)) {
      return;
    }

    const networks = TransactionProviderFactory.getSupportedNetworks();
    const syncPromises = networks.map(network =>
      this.syncNetwork(walletAddress, network)
    );

    try {
      await Promise.allSettled(syncPromises);
      console.log(`[TransactionSync] Completed sync for wallet ${walletAddress}`);
    } catch (error) {
      console.error(`[TransactionSync] Error syncing wallet ${walletAddress}:`, error);
    }
  }

  /**
   * Sincroniza transacciones para una red específica
   */
  async syncNetwork(walletAddress: string, network: string): Promise<Transaction[]> {
    const syncKey = `${walletAddress}-${network}`;

    // Evitar sincronizaciones concurrentes
    if (this.isSyncing.get(syncKey)) {
      console.log(`[TransactionSync] Sync already in progress for ${syncKey}`);
      return [];
    }

    this.isSyncing.set(syncKey, true);

    try {
      // Verificar si el cache está desactualizado
      if (!TransactionCacheService.isCacheStale(walletAddress, network)) {
        console.log(`[TransactionSync] Cache is fresh for ${syncKey}`);
        return this.getFilteredTransactions(walletAddress, network);
      }

      console.log(`[TransactionSync] Syncing ${syncKey}`);

      const provider = TransactionProviderFactory.getProvider(network);
      const lastBlock = TransactionCacheService.getLastSyncedBlock(walletAddress, network);

      // Obtener transacciones desde el último bloque sincronizado
      const [transactions, tokenTransfers] = await Promise.all([
        provider.getTransactions(walletAddress, lastBlock > 0 ? lastBlock + 1 : undefined),
        provider.getTokenTransfers(walletAddress),
      ]);

      // Combinar transacciones normales y de tokens
      const allTransactions = [...transactions, ...tokenTransfers];

      // Eliminar duplicados
      const uniqueTransactions = this.removeDuplicates(allTransactions);

      if (uniqueTransactions.length > 0) {
        // Guardar en cache
        const highestBlock = this.getHighestBlockNumber(uniqueTransactions);
        TransactionCacheService.saveTransactions(
          walletAddress,
          network,
          uniqueTransactions,
          Math.max(lastBlock, highestBlock)
        );

        console.log(`[TransactionSync] Synced ${uniqueTransactions.length} transactions for ${syncKey}`);
      }

      const freshTransactions = this.getFilteredTransactions(walletAddress, network);

      // Empuja directo al store si es la wallet/red activa — no depende de que algún componente siga escuchando el evento.
      const state = useWalletStore.getState();
      if (
        state.address?.toLowerCase() === walletAddress.toLowerCase() &&
        state.selectedNetwork === network
      ) {
        state.setTransactions(freshTransactions);
      }

      // Notificar a los componentes React que hay datos nuevos (aunque sea 0 nuevas)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('transactions:synced', {
          detail: { walletAddress, network }
        }));
      }

      return freshTransactions;
    } catch (error) {
      console.error(`[TransactionSync] Error syncing ${syncKey}:`, error);
      return this.getFilteredTransactions(walletAddress, network);
    } finally {
      this.isSyncing.set(syncKey, false);
    }
  }

  /**
   * Agrega una transacción enviada por el usuario
   */
  addUserTransaction(walletAddress: string, network: string, transaction: Transaction): void {
    console.log(`[TransactionSync] Adding user transaction ${transaction.hash}`);

    // Marcar como pendiente inicialmente
    const pendingTransaction = {
      ...transaction,
      status: 'pending' as const,
      source: 'user_sent' as const,
      timestamp: Date.now(),
    };

    TransactionCacheService.addTransaction(walletAddress, network, pendingTransaction);

    // Programar verificación de estado
    this.scheduleTransactionStatusCheck(walletAddress, network, transaction.hash);
  }

  /**
   * Obtiene transacciones desde el cache, sin las de contratos reemplazados.
   */
  getTransactions(walletAddress: string, network: string): Transaction[] {
    return this.getFilteredTransactions(walletAddress, network);
  }

  /** Cache read + filtro de contratos deprecados, para no repetirlo en cada call site. */
  private getFilteredTransactions(walletAddress: string, network: string): Transaction[] {
    const transactions = TransactionCacheService.getTransactions(walletAddress, network);
    if (DEPRECATED_TOKEN_ADDRESSES.length === 0) return transactions;

    const deprecated = new Set(DEPRECATED_TOKEN_ADDRESSES.map((a) => a.toLowerCase()));
    return transactions.filter(
      (tx) => !tx.tokenAddress || !deprecated.has(tx.tokenAddress.toLowerCase())
    );
  }

  /**
   * Obtiene transacciones pendientes
   */
  getPendingTransactions(walletAddress: string, network: string): Transaction[] {
    return TransactionCacheService.getPendingTransactions(walletAddress, network);
  }

  /**
   * Fuerza una sincronización inmediata
   */
  async forcSync(walletAddress: string, network?: string): Promise<void> {
    if (network) {
      await this.syncNetwork(walletAddress, network);
    } else {
      await this.syncAllNetworks(walletAddress);
    }
  }

  /**
   * Limpia el cache y detiene la sincronización
   */
  cleanup(walletAddress?: string): void {
    if (walletAddress) {
      this.stopAutoSync(walletAddress);
      TransactionCacheService.clearWalletCache(walletAddress);
    } else {
      // Limpiar todo
      this.syncIntervals.forEach((_interval, address) => {
        this.stopAutoSync(address);
      });
      TransactionCacheService.clearAllCache();
    }
  }

  // Métodos privados

  private shouldSync(walletAddress: string): boolean {
    // Solo sincronizar si la wallet está desbloqueada
    const walletManager = WalletManager.getInstance();
    if (!walletManager.isUnlocked()) {
      return false;
    }

    // Verificar que la dirección coincida con la wallet activa
    const activeAddress = walletManager.getActiveAddress();
    return activeAddress?.toLowerCase() === walletAddress.toLowerCase();
  }

  private getSyncInterval(walletAddress: string): number {
    // Verificar si hay transacciones pendientes
    const networks = TransactionProviderFactory.getSupportedNetworks();
    const hasPendingTxs = networks.some(network =>
      TransactionCacheService.getPendingTransactions(walletAddress, network).length > 0
    );

    if (hasPendingTxs) {
      return SYNC_CONFIG.PENDING_INTERVAL; // 30 segundos
    }

    // Verificar inactividad del usuario
    const lastActivity = parseInt(sessionStorage.getItem('last_user_activity') || '0');
    const inactiveTime = Date.now() - lastActivity;

    if (inactiveTime > 10 * 60 * 1000) { // 10 minutos de inactividad
      return SYNC_CONFIG.INACTIVE_INTERVAL; // 5 minutos
    }

    return SYNC_CONFIG.INTERVAL; // 1 minuto normal
  }

  private removeDuplicates(transactions: Transaction[]): Transaction[] {
    const seen = new Set<string>();
    return transactions.filter(tx => {
      // 1. Filtrar transacciones nativas con valor 0
      // Suelen ser llamadas a contratos (como para enviar tokens) que ya muestran su propia entrada de token.
      if (!tx.tokenAddress && (tx.value === '0' || tx.value === '0.0' || tx.value === '0x0')) {
        return false;
      }

      // 2. Usar una clave compuesta (hash + moneda/token) para no eliminar el token si comparte hash con el gas
      const uniqueId = `${tx.hash}-${tx.tokenAddress || 'native'}`;
      if (seen.has(uniqueId)) {
        return false;
      }
      seen.add(uniqueId);
      return true;
    });
  }

  private getHighestBlockNumber(transactions: Transaction[]): number {
    return transactions.reduce((max, tx) => Math.max(max, tx.blockNumber ?? 0), 0);
  }

  /**
   * Verifica el estado de una transacción pendiente con reintentos exponenciales.
   *
   * getTransactionDetails en cada provider intenta el servidor primero.
   * y usa el RPC directo como fallback si el servidor no está disponible.
   * Retorna null cuando la tx sigue pendiente, o un Transaction con el status final.
   */
  private scheduleTransactionStatusCheck(walletAddress: string, network: string, txHash: string): void {
    // Intervalos de reintento: 30s, 60s, 120s, 240s (backoff exponencial)
    const retryDelays = [30_000, 60_000, 120_000, 240_000];
    let attemptIndex = 0;

    const checkStatus = async (): Promise<void> => {
      try {
        const provider = TransactionProviderFactory.getProvider(network);
        const txDetails = await provider.getTransactionDetails(txHash);

        if (txDetails) {
          // La tx fue confirmada o falló — actualizar cache y terminar polling
          TransactionCacheService.updateTransactionStatus(
            walletAddress,
            network,
            txHash,
            txDetails.status,
            txDetails.blockNumber
          );
          console.log(`[TransactionSync] Transaction ${txHash} resolved: ${txDetails.status}`);
          return;
        }

        // La tx sigue pendiente — reintentar si quedan intentos
        attemptIndex++;
        if (attemptIndex < retryDelays.length) {
          console.log(`[TransactionSync] Tx ${txHash} still pending, retrying in ${retryDelays[attemptIndex] / 1000}s (attempt ${attemptIndex + 1}/${retryDelays.length})`);
          setTimeout(checkStatus, retryDelays[attemptIndex]);
        } else {
          // Agotamos los reintentos — marcar como fallida para no dejar la tx colgada
          console.warn(`[TransactionSync] Tx ${txHash} unresolved after all retries, marking as failed`);
          TransactionCacheService.updateTransactionStatus(walletAddress, network, txHash, 'failed');
        }
      } catch (error) {
        console.error(`[TransactionSync] Error checking transaction status for ${txHash}:`, error);

        // Reintentar incluso ante errores inesperados
        attemptIndex++;
        if (attemptIndex < retryDelays.length) {
          setTimeout(checkStatus, retryDelays[attemptIndex]);
        }
      }
    };

    // Primer intento a los 30 segundos
    setTimeout(checkStatus, retryDelays[0]);
  }
}